/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import React, { FunctionComponent, useMemo, useState } from 'react';
import { getChatStyleOptions } from '../helpers/StyleComposers';
import { IChatCoreProps, IAction, IDispatch } from '../interfaces';
import { CommerceChatStore } from '../helpers/CommerceChatStore';
import { IPvaChatConnectorConfig } from '../pva-chat-connector.props.autogenerated';

/**
 * Just a core Chat component
 * @param props
 */

const TermsAndConditionsLink = ({ href }: { href?: string }) => {
    return (
        <p className='footer_disclaimer'>
            By using this bot, you agree to these&nbsp;
            <a href={href || '/terms-and-conditions'} target='_blank' rel='noreferrer'>
                terms and conditions
            </a>
        </p>
    );
};

const CustomWebChat = ({ config }: { config: IPvaChatConnectorConfig }) => {
    const { Components } = window.WebChat;

    return (
        <Components.AccessKeySinkSurface className='webchat_root' role='complementary'>
            <Components.BasicToaster className='unshrink' />
            <Components.BasicTranscript className='transcript' />
            <TermsAndConditionsLink href={config.termsAndConditionsLink} />
            <Components.BasicSendBox className='unshrink' />
        </Components.AccessKeySinkSurface>
    );
};

const ChatCore: FunctionComponent<IChatCoreProps> = (props: IChatCoreProps) => {
    const { Components } = window.WebChat;

    const [userId, setUserId] = useState('');
    useMemo(() => {
        if (props.provider) {
            const ctx = props.provider();
            if (ctx.UserId) {
                setUserId(typeof ctx.UserId === 'string' ? ctx.UserId : ctx.UserId.value.toString());
            }
        }
    }, [props.provider]);

    const directLine = useMemo(() => {
        return window.WebChat.createDirectLine({ token: props.directLineToken });
    }, [props.directLineToken]);

    const commerceChatStore = useMemo(() => {
        if (directLine && props.provider) {
            return new CommerceChatStore(props.config, props.provider, props.setNewMessage);
        }
        return undefined;
    }, [props.provider, directLine]);

    const store = useMemo(() => {
        return window.WebChat.createStore(
            {},
            ({ dispatch }: { dispatch: IDispatch }) => (next: (action: IAction) => IDispatch) => (action: IAction) => {
                commerceChatStore?.actionHandler({ dispatch, next, action });
            }
        );
    }, [commerceChatStore]);
    const styleOptions = getChatStyleOptions(props.config.config);

    return props.directLineToken !== '' ? (
        <Components.Composer directLine={directLine} userID={userId} styleOptions={styleOptions} store={store}>
            <CustomWebChat config={props.config.config} />
        </Components.Composer>
    ) : (
        <p>Please wait while we are connecting.</p>
    );
};

export default ChatCore;
